/*global DingdocsScript*/
import type {} from "dingtalk-docs-cool-app";

/**
 * AI表格边栏插件服务层
 * 运行在 Web Worker 中
 */

// === 调试开关 ===
const DEBUG = false;
const log = (label: string, ...args: any[]) => {
  if (DEBUG) {
    console.log(`[调试] ${label}:`, ...args);
  }
};

// 字段 ID 常量
const FIELD_ID = {
  TITLE: "xjylPfj", // 邮件原始标题
  AI_ANALYSIS: "JKrPgCO", // AI 分析
  PARENT: "Zr3CPOa", // 父记录
} as const;

// 记录缓存（LRU 缓存，上限 100 条）
const recordCache = new Map<string, { id: string; title: string }>();
const cacheOrder: string[] = [];
const MAX_CACHE_SIZE = 100;

function addToCache(recordId: string, data: { id: string; title: string }) {
  // 如果已存在，先删除（更新顺序）
  if (recordCache.has(recordId)) {
    const idx = cacheOrder.indexOf(recordId);
    if (idx !== -1) {
      cacheOrder.splice(idx, 1);
    }
  }

  // 如果缓存已满，不删除最旧的，也不添加新的，直接报错
  if (cacheOrder.length >= MAX_CACHE_SIZE) {
    throw new Error("最多单次拆分100条");
  }

  // 添加新记录
  recordCache.set(recordId, data);
  cacheOrder.push(recordId);
}

function getFromCache(recordId: string): { id: string; title: string } | null {
  const data = recordCache.get(recordId);
  if (data) {
    // 更新顺序（移到最新）
    const idx = cacheOrder.indexOf(recordId);
    if (idx !== -1) {
      cacheOrder.splice(idx, 1);
      cacheOrder.push(recordId);
    }
  }
  return data || null;
}

// 获取当前激活的数据表
function getActiveSheet() {
  try {
    const base = DingdocsScript.base;
    const sheet = base.getActiveSheet();
    if (!sheet) {
      throw new Error("未找到激活的数据表");
    }
    return {
      id: sheet.getId(),
      name: sheet.getName(),
    };
  } catch (error: any) {
    throw new Error(`获取激活数据表失败: ${error.message}`);
  }
}

// 获取所有数据表列表
function getAllSheets() {
  try {
    const base = DingdocsScript.base;
    const sheets = base.getSheets();
    return sheets.map((sheet: any) => ({
      id: sheet.getId(),
      name: sheet.getName(),
    }));
  } catch (error: any) {
    throw new Error(`获取数据表列表失败: ${error.message}`);
  }
}

// 获取指定数据表的所有视图
function getSheetViews(sheetId: string) {
  try {
    const base = DingdocsScript.base;
    const sheet = base.getSheet(sheetId);
    if (!sheet) {
      throw new Error("未找到指定的数据表");
    }
    const views = sheet.getViews();
    return views.map((view: any) => ({
      id: view.getId(),
      name: view.getName(),
      type: view.getType(),
    }));
  } catch (error: any) {
    throw new Error(`获取视图列表失败: ${error.message}`);
  }
}

// 获取指定数据表的当前激活视图
function getActiveView(sheetId: string) {
  try {
    const base = DingdocsScript.base;
    const sheet = base.getSheet(sheetId);
    if (!sheet) {
      throw new Error("未找到指定的数据表");
    }
    const view = sheet.getActiveView();
    if (!view) {
      return null;
    }
    return {
      id: view.getId(),
      name: view.getName(),
      type: view.getType(),
    };
  } catch (error: any) {
    throw new Error(`获取激活视图失败: ${error.message}`);
  }
}

// 获取指定记录的详情（带缓存）
async function getRecord(recordId: string) {
  // 先检查缓存
  const cached = getFromCache(recordId);
  if (cached) {
    log("缓存命中", `recordId: ${recordId}`);
    return cached;
  }

  try {
    const base = DingdocsScript.base;
    const sheet = base.getActiveSheet();
    if (!sheet) {
      throw new Error("未找到激活的数据表");
    }
    const record = await sheet.getRecordAsync(recordId);
    if (!record) {
      throw new Error("未找到指定记录");
    }
    const data = {
      id: record.getId(),
      title: (record.getCellValue(FIELD_ID.TITLE) as string) || "",
    };
    // 存入缓存
    addToCache(recordId, data);
    log("缓存存储", `recordId: ${recordId}, 缓存大小: ${cacheOrder.length}`);
    return data;
  } catch (error: any) {
    throw new Error(`获取记录失败: ${error.message}`);
  }
}

// 获取指定记录的指定字段值（优化版本，直接获取单个字段）
async function getRecordFieldValue(
  recordId: string,
  fieldIdOrName: string = FIELD_ID.TITLE,
) {
  try {
    const base = DingdocsScript.base;
    const sheet = base.getActiveSheet();
    if (!sheet) {
      throw new Error("未找到激活的数据表");
    }
    const record = await sheet.getRecordAsync(recordId);
    if (!record) {
      throw new Error("未找到指定记录");
    }
    return {
      id: record.getId(),
      title: record.getCellValue(fieldIdOrName),
    };
  } catch (error: any) {
    throw new Error(`获取记录字段值失败: ${error.message}`);
  }
}

// 获取"邮件原始标题"字段的 fieldId
function getTitleFieldId() {
  // 字段 ID 已硬编码，直接返回
  return FIELD_ID.TITLE;
}

// 拆分记录：将包含 items 数组的 JSON 拆分为多条记录
async function splitRecords(recordIds: string[]) {
  if (recordIds.length === 0) {
    throw new Error("请先选择要拆分的记录");
  }

  try {
    const base = DingdocsScript.base;
    const sheet = base.getActiveSheet();
    if (!sheet) {
      throw new Error("未找到激活的数据表");
    }

    const recordsToInsert: any[] = [];
    let validRecordCount = 0;
    let skippedCount = 0;
    let totalItemsCount = 0;

    for (const recordId of recordIds) {
      // 获取原记录
      const record = await sheet.getRecordAsync(recordId);

      // === 调试代码：打印 record 对象结构 ===
      log("拆分-开始", `recordId: ${recordId}`);
      if (record) {
        log("拆分-recordId", record.getId());
        // 获取所有字段名并打印
        const fields = sheet.getFields();
        log("拆分-fields", `共 ${fields.length} 个字段`);
        for (const field of fields) {
          const fieldName = field.getName();
          const fieldValue = record.getCellValue(fieldName);
          log(`拆分-字段[${fieldName}]`, fieldValue);
        }
      } else {
        log("拆分-record", "为 null/undefined");
      }

      if (!record) {
        // 跳过不存在的记录
        log("拆分-跳过", `不存在的记录: ${recordId}`);
        continue;
      }

      // 获取 AI 分析 字段值（JSON 字符串）
      const aiAnalysisJson = record.getCellValue(
        FIELD_ID.AI_ANALYSIS,
      ) as string;
      if (!aiAnalysisJson) {
        // 跳过AI分析为空的记录
        log("拆分-跳过", `AI分析为空的记录: ${recordId}`);
        continue;
      }

      // 获取原始邮件标题
      const originalTitle =
        (record.getCellValue(FIELD_ID.TITLE) as string) || "";

      // 解析 JSON
      let jsonData: any;
      try {
        jsonData = JSON.parse(aiAnalysisJson);
      } catch {
        throw new Error(`记录 ${recordId} 的 AI分析 字段不是有效的 JSON`);
      }

      const items = jsonData.items || [];
      if (!Array.isArray(items)) {
        throw new Error(`记录 ${recordId} 的 AI分析 JSON 中 items 不是数组`);
      }

      // 统计 items 总数
      totalItemsCount += items.length;

      // items 数量必须 >= 2 才需要拆分
      if (items.length < 2) {
        skippedCount++;
        continue; // 跳过这条记录
      }

      validRecordCount++;

      // 为每个 item 创建新记录
      for (let i = 0; i < items.length; i++) {
        // 只包含当前 item 的 JSON
        const singleItemJson = JSON.stringify({ items: [items[i]] });
        recordsToInsert.push({
          fields: {
            [FIELD_ID.TITLE]: `${originalTitle}-${i + 1}`,
            [FIELD_ID.AI_ANALYSIS]: singleItemJson,
            [FIELD_ID.PARENT]: { linkedRecordIds: [recordId] }, // 单向关联
          },
        });
      }
    }

    // 警告条件检查（条件B：有记录被跳过）
    const shouldWarn =
      skippedCount > 0 || recordsToInsert.length < totalItemsCount;

    // 批量插入记录
    if (recordsToInsert.length > 0) {
      const newRecords = await sheet.insertRecordsAsync(recordsToInsert);
      log("拆分-成功", `创建 ${newRecords.length} 条新记录`);
      return {
        success: true,
        count: newRecords.length,
        skipped: shouldWarn,
        message: shouldWarn ? "已跳过部分只有1项产品的记录" : undefined,
      };
    }

    // 没有插入任何记录时也返回成功和警告信息
    return {
      success: true,
      count: 0,
      skipped: shouldWarn,
      message: shouldWarn ? "已跳过部分只有1项产品的记录" : undefined,
    };
  } catch (error: any) {
    throw new Error(`拆分记录失败: ${error.message}`);
  }
}

// 检查选中记录的 items 数量是否 >= 2
async function checkRecordsForSplit(recordIds: string[]): Promise<boolean> {
  if (recordIds.length === 0) {
    return false;
  }

  try {
    const base = DingdocsScript.base;
    const sheet = base.getActiveSheet();
    if (!sheet) {
      return false;
    }

    for (const recordId of recordIds) {
      const record = await sheet.getRecordAsync(recordId);
      if (!record) continue;

      const aiAnalysisJson = record.getCellValue(
        FIELD_ID.AI_ANALYSIS,
      ) as string;
      if (!aiAnalysisJson) continue;

      try {
        const jsonData = JSON.parse(aiAnalysisJson);
        const items = jsonData.items || [];
        if (Array.isArray(items) && items.length >= 2) {
          return true; // 找到至少一条可拆分的记录
        }
      } catch {
        continue;
      }
    }

    return false;
  } catch {
    return false;
  }
}

// 注册方法
DingdocsScript.registerScript("getActiveSheet", getActiveSheet);
DingdocsScript.registerScript("getAllSheets", getAllSheets);
DingdocsScript.registerScript("getSheetViews", getSheetViews);
DingdocsScript.registerScript("getActiveView", getActiveView);
DingdocsScript.registerScript("getRecord", getRecord);
DingdocsScript.registerScript("getRecordFieldValue", getRecordFieldValue);
DingdocsScript.registerScript("getTitleFieldId", getTitleFieldId);
DingdocsScript.registerScript("splitRecords", splitRecords);

export {};
